/**
 * Work Atlas (docs/design/tide.md, scope item 3) - answers one spatial
 * question: where across Florida is open work concentrated, by facility, and
 * what kind of work is it?
 *
 *   header      "Open work by facility, now" - the window does not apply
 *   legend      PA / UMED / Manifest, toggles a category in every column and in the list totals
 *   scale bar   the one linear scale every column uses
 *   map         Leaflet + an SVG prism per facility (PrismLayer)
 *   detail      the selected facility, with "Show these records"
 *   not located open work that cannot be placed, never dropped
 *   list        the ranked 2D equivalent (keyboard and screen-reader path)
 *
 * 3D is used because the question is spatial. Heights are linear in count on
 * one stated scale; the list carries the exact figures. Data contract:
 * docs/record-flow/api-contract.md, "Atlas (Tide phase 1)".
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import LivingLegend from '../../../../components/tide/LivingLegend';
import { DOMAIN } from '../../../../components/tide/tokens';
import { Marker } from '../../../../components/tide/Marker';
import type { WindowParams } from '../flowApi';
import { useReportFreshness } from '../flowUi';
import { ATLAS_DOMAINS, fetchAtlas, type Atlas, type AtlasDomain, type AtlasFacility, type NotLocatedReason } from './atlasApi';
import AtlasMap from './AtlasMap';
import AtlasList from './AtlasList';
import { diamond, pxPerRecord, scaleBar, sideFaces } from './prismGeometry';
import './atlas.css';

export interface AtlasRecordsTarget { id: string; name: string }

const DESCRIPTION: Record<AtlasDomain, string> = {
  PA: 'Open PA cases (advocate cases not closed).',
  UMED: 'Open UMED cases (not closed).',
  MANIFEST: 'Undecided residents on open manifest reviews.',
};

const REASON_TEXT: Record<NotLocatedReason, (n: string) => string> = {
  NO_FACILITY: (n) => `${n} name no facility`,
  UNKNOWN_FACILITY: (n) => `${n} name a facility record that does not exist`,
  MERGED: (n) => `${n} sit at facility records merged into another`,
  NO_COORDINATES: (n) => `${n} sit at facilities with no coordinates`,
  OUT_OF_BOUNDS: (n) => `${n} sit at facilities whose coordinates fall outside Florida`,
};

const fmt = (n: number) => n.toLocaleString('en-US');
const sum3 = (c: Record<AtlasDomain, number>) => c.PA + c.UMED + c.MANIFEST;
const fmtAsOf = (iso: string) => new Date(iso).toLocaleString('en-US', {
  timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
});
const fmtDate = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });

/** True when the OS asks for reduced motion; follows changes while mounted. */
export function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const read = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
  const [reduce, setReduce] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const on = () => setReduce(mq.matches);
    if (typeof mq.addEventListener === 'function') { mq.addEventListener('change', on); return () => mq.removeEventListener('change', on); }
    if (typeof mq.addListener === 'function') { mq.addListener(on); return () => mq.removeListener(on); }
    return undefined;
  }, []);
  return reduce;
}

type Load = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; data: Atlas };

export default function AtlasView({ window: w, onShowRecords }: {
  window: WindowParams;
  /** Switch to the Records tab filtered to this facility (RecordFlow wires it, as People does for a person). */
  onShowRecords?(facility: AtlasRecordsTarget): void;
}) {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  const [shown, setShown] = useState<AtlasDomain[]>(ATLAS_DOMAINS);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [growKey, setGrowKey] = useState(0);
  const reduceMotion = usePrefersReducedMotion();
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Set by a selection, consumed by the detail panel when it mounts or changes facility:
  // focus goes to its heading once, so re-sorting or toggling never steals focus.
  const focusDetail = useRef(false);
  // The facility whose list row should take focus back after Clear selection.
  const [restoreTo, setRestoreTo] = useState<string | null>(null);

  // The Record Flow header band shows when the visible tab's figures were generated.
  useReportFreshness(load.status === 'ready' ? { generatedAt: load.data.generatedAt, timeZone: 'America/New_York' } : null);

  const select = (id: string) => { focusDetail.current = true; setSelectedId(id); };
  const clear = () => { focusDetail.current = false; setRestoreTo(selectedId); setSelectedId(null); };
  const takeFocus = () => { const t = focusDetail.current; focusDetail.current = false; return t; };

  useEffect(() => {
    if (!restoreTo || !rootRef.current) return;
    const row = Array.from(rootRef.current.querySelectorAll<HTMLButtonElement>('[data-row-button]'))
      .find((b) => b.getAttribute('data-row-button') === restoreTo);
    row?.focus();
    setRestoreTo(null);
  }, [restoreTo]);

  useEffect(() => {
    let live = true;
    setLoad({ status: 'loading' });
    fetchAtlas().then((r) => {
      if (!live) return;
      setLoad(r.ok ? { status: 'ready', data: r.data } : { status: 'error', message: r.error });
    });
    return () => { live = false; };
  }, [nonce]);

  const toggle = (keys: string[]) => {
    setShown(ATLAS_DOMAINS.filter((d) => keys.includes(d)));
    setGrowKey((g) => g + 1);
  };

  return (
    <div className="atlas" data-testid="atlas" ref={rootRef}>
      {/* A plain div, not <header>: the page already has its banner landmark. */}
      <div className="atlas-head">
        <h2 id="atlas-title" className="atlas-title">Open work by facility, now</h2>
        <p className="atlas-sub">
          A snapshot of what is open at this moment
          {load.status === 'ready' ? `, as of ${fmtAsOf(load.data.generatedAt)}` : ''}.
          {' '}The window above ({fmtDate(w.from)} to {fmtDate(w.to)}) does not apply here: open work has no window.
        </p>
      </div>

      {load.status === 'loading' && <AtlasSkeleton />}
      {load.status === 'error' && (
        <div className="tide-state tide-state--error" role="alert">
          <span className="tide-state__head">
            <Marker shape="triangle" color="var(--tide-status-danger)" size={12} />
            The atlas did not load
          </span>
          <p className="tide-state__msg">{load.message}</p>
          <button type="button" className="tide-state__btn" onClick={() => setNonce((n) => n + 1)}>Try again</button>
        </div>
      )}
      {load.status === 'ready' && (
        <AtlasReady
          data={load.data}
          shown={shown}
          onToggle={toggle}
          selectedId={selectedId}
          onSelect={select}
          onClear={clear}
          takeFocus={takeFocus}
          animate={!reduceMotion}
          growKey={growKey}
          window={w}
          onShowRecords={onShowRecords}
        />
      )}
    </div>
  );
}

function AtlasReady({ data, shown, onToggle, selectedId, onSelect, onClear, takeFocus, animate, growKey, window: w, onShowRecords }: {
  data: Atlas; shown: AtlasDomain[]; onToggle(keys: string[]): void;
  selectedId: string | null; onSelect(id: string): void; onClear(): void; takeFocus(): boolean;
  animate: boolean; growKey: number; window: WindowParams;
  onShowRecords?(facility: AtlasRecordsTarget): void;
}) {
  const grand = sum3(data.totals);
  const maxFacility = useMemo(
    () => data.facilities.reduce<AtlasFacility | null>((m, f) => (!m || f.total > m.total ? f : m), null),
    [data.facilities],
  );
  const maxTotal = maxFacility?.total || 0;
  const k = pxPerRecord(maxTotal);
  const bar = scaleBar(k);
  const selected = data.facilities.find((f) => f.id === selectedId) || null;

  const items = ATLAS_DOMAINS.map((d) => ({
    key: d,
    label: DOMAIN[d].label,
    color: DOMAIN[d].color,
    marker: DOMAIN[d].marker,
    description: DESCRIPTION[d],
    value: data.totals[d],
    unit: 'open records',
    share: grand > 0 ? { of: grand, label: 'all open work' } : null,
  }));

  const mapMessage = data.facilities.length > 0 ? undefined : grand === 0
    ? 'No open PA, UMED or Manifest work anywhere right now.'
    : `None of the ${fmt(grand)} open records can be placed on the map. See below for where they are.`;

  return (
    <>
      <div className="atlas-keys">
        <LivingLegend
          title="Open work, by kind"
          items={items}
          mode="toggle"
          selected={shown}
          onChange={onToggle}
          scope="Hides or shows that kind of work in every column on the map and in the list totals. These counts always cover all open work, on the map or not."
          className="atlas-legend"
          stacked
          density="compact"
        />
        <ScaleBar k={k} records={bar.records} px={bar.px} tallest={maxFacility} />
      </div>

      <AtlasMap
        facilities={data.facilities}
        shown={shown}
        k={k}
        selectedId={selectedId}
        onSelect={(id) => onSelect(id)}
        animate={animate}
        growKey={growKey}
        message={mapMessage}
      />

      <NotLocated data={data} />

      {data.facilities.length > 0 && (
        <AtlasList
          facilities={data.facilities}
          shown={shown}
          selectedId={selectedId}
          onSelect={onSelect}
          maxTotal={maxTotal}
          hint={selected ? null : 'Choose a column on the map, or a facility below, to see its detail.'}
          renderDetail={(f) => (
            <FacilityDetail facility={f} shown={shown} window={w} onShowRecords={onShowRecords} onClear={onClear} takeFocus={takeFocus} />
          )}
        />
      )}
    </>
  );
}

function ScaleBar({ k, records, px, tallest }: { k: number; records: number; px: number; tallest: AtlasFacility | null }) {
  const faces = sideFaces(0, px);
  const perPx = 1 / k;
  return (
    <figure className="atlas-scale" data-testid="atlas-scale" aria-label="Column scale">
      {/* Drawn at exactly `px` tall, with the same geometry as the columns on the map. */}
      <svg width={24} height={px + 16} viewBox={`-12 ${-px - 8} 24 ${px + 16}`} aria-hidden="true" focusable="false" className="atlas-scale__glyph" data-px={px}>
        <polygon points={faces.left} className="atlas-scale__face" />
        <polygon points={faces.right} className="atlas-scale__face atlas-scale__face--dark" />
        <polygon points={diamond(px)} className="atlas-scale__face atlas-scale__face--top" />
      </svg>
      <figcaption>
        <span className="atlas-scale__big">A column this tall = {fmt(records)} open {records === 1 ? 'record' : 'records'}</span>
        <span className="atlas-scale__small">
          Heights are linear, on one scale for every facility: 1 px = {perPx < 10 ? perPx.toFixed(2) : fmt(Math.round(perPx))} records.
          Zooming the map does not change it, and hiding a kind of work does not rescale it.
          {tallest && ` Tallest: ${tallest.name}, ${fmt(tallest.total)} open.`}
        </span>
      </figcaption>
    </figure>
  );
}

function FacilityDetail({ facility: f, shown, window: w, onShowRecords, onClear, takeFocus }: {
  facility: AtlasFacility; shown: AtlasDomain[]; window: WindowParams;
  onShowRecords?(facility: AtlasRecordsTarget): void; onClear(): void; takeFocus(): boolean;
}) {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  // After a selection, focus lands on this heading (screen readers announce it); Tab then
  // reaches Clear selection and Show these records. Runs after every render but acts only
  // when a selection asked for it, so a re-sort or a legend toggle never moves focus.
  useEffect(() => {
    if (takeFocus()) headingRef.current?.focus();
  });
  return (
    <section className="atlas-detail" aria-labelledby="atlas-detail-name" data-testid="atlas-detail">
      <div className="atlas-detail__head">
        <div>
          <h3 id="atlas-detail-name" ref={headingRef} tabIndex={-1} className="atlas-h3 atlas-detail__name">{f.name}</h3>
          <p className="atlas-detail__region">{f.region || 'Region not recorded'}</p>
        </div>
        <button type="button" className="atlas-link-btn" onClick={onClear}>Clear selection</button>
      </div>
      <dl className="atlas-detail__grid">
        {ATLAS_DOMAINS.map((d) => (
          <div key={d} className="atlas-detail__cell" data-hidden={shown.includes(d) ? undefined : 'true'}>
            <dt><Marker shape={DOMAIN[d].marker} color={DOMAIN[d].color} size={12} /> {DOMAIN[d].label}{shown.includes(d) ? '' : ' (hidden on the map)'}</dt>
            <dd>{fmt(f.open[d])}</dd>
          </div>
        ))}
        <div className="atlas-detail__cell">
          <dt>All open work</dt>
          <dd>{fmt(f.total)}</dd>
        </div>
        <div className="atlas-detail__cell atlas-detail__cell--wide">
          <dt>Oldest open</dt>
          <dd>
            {f.oldestOpenDays == null ? <span className="atlas-na">not recorded</span> : `${f.oldestOpenDays.toFixed(1)} days in its current stage`}
            {(f.unaged ?? 0) > 0 && (
              <span className="atlas-detail__aside">
                {fmt(f.unaged || 0)} open {(f.unaged || 0) === 1 ? 'record has' : 'records have'} no recorded stage-entry time and {(f.unaged || 0) === 1 ? 'is' : 'are'} not counted in this.
              </span>
            )}
          </dd>
        </div>
      </dl>
      {onShowRecords && (
        <div className="atlas-detail__actions">
          <button type="button" className="atlas-primary-btn" onClick={() => onShowRecords({ id: f.id, name: f.name })}>
            Show these records
          </button>
          <p className="atlas-note">
            Opens Records filtered to this facility: the steps recorded there from {fmtDate(w.from)} to {fmtDate(w.to)}.
            Records lists movements, so its rows are not these {fmt(f.total)} open records one for one.
          </p>
        </div>
      )}
    </section>
  );
}

function NotLocated({ data }: { data: Atlas }) {
  const n = sum3(data.notLocated.open);
  if (n === 0) {
    return <p className="atlas-located atlas-located--all" data-testid="atlas-not-located">Every open record is placed on the map.</p>;
  }
  const reasons = Object.entries(data.notLocated.reasons || {})
    .filter(([, v]) => (v || 0) > 0)
    .map(([r, v]) => REASON_TEXT[r as NotLocatedReason]?.(fmt(v || 0)))
    .filter(Boolean);
  return (
    <p className="atlas-located" data-testid="atlas-not-located">
      <Marker shape="dash" color="var(--tide-status-missing)" size={12} />{' '}
      <strong>{fmt(n)} open {n === 1 ? 'record is' : 'records are'} not on the map</strong>
      {' '}({ATLAS_DOMAINS.map((d) => `${DOMAIN[d].label} ${fmt(data.notLocated.open[d])}`).join(', ')}
      {data.notLocated.facilities > 0 ? `, at ${fmt(data.notLocated.facilities)} ${data.notLocated.facilities === 1 ? 'facility' : 'facilities'}` : ''}).
      {reasons.length > 0 && ` Of these, ${reasons.join('; ')}.`}
      {' '}They are counted in the legend totals, not dropped.
    </p>
  );
}

function AtlasSkeleton() {
  return (
    <div className="tide-state tide-state--loading" aria-busy="true" data-testid="atlas-loading">
      <p className="tide-state__loading-label" role="status">Loading open work by facility...</p>
      <div className="tide-skel tide-skel--row" />
      <div className="tide-skel atlas-skel-map" />
      <div className="tide-skel tide-skel--row" />
      <div className="tide-skel tide-skel--row" />
    </div>
  );
}
